import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Alert,
  Button,
  InlineExternalLink,
  Modal,
  ModalAction,
  ModalSection,
  useMedia,
} from '@monash/portal-react';
import { useEffect, useState, useContext } from 'react';
import SortableItem from '../sortable-item/SortableItem';
import { APIContext } from '@monash/portal-frontend-common';
import c from './category-reorder.module.scss';
import { useSnackbar } from '../../../providers/SnackbarProvider';

const CategoryReorder = ({ isOpen, setIsOpen, sections, setCatOrder }) => {
  const { updateCategoriesOrder } = useContext(APIContext);
  const { addSnackbar } = useSnackbar();

  const size = useMedia();
  const ctaButtonSize = size === 'S' ? 'small' : 'medium';

  const assembleOrder = () =>
    sections.map((s) => {
      return {
        type: s.type,
        order: s.items.map((i) => i.id),
      };
    });

  const [order, setOrder] = useState(assembleOrder());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setOrder(assembleOrder());
    setError(null);
  }, [isOpen]);

  const names = sections.reduce((acc, section) => {
    section.items.forEach((item) => {
      acc[item.id] = item.title;
    });
    return acc;
  }, {});

  const swap = (active, over) => {
    if (
      active.id === over.id ||
      active.data.current.section !== over.data.current.section
    )
      return;

    const sectionIndex = order.findIndex(
      (s) => s.type === active.data.current.section
    );
    const activeIndex = order[sectionIndex].order.findIndex(
      (i) => i === active.id
    );
    const overIndex = order[sectionIndex].order.findIndex((i) => i === over.id);
    const newOrder = [...order];
    newOrder[sectionIndex].order = arrayMove(
      newOrder[sectionIndex].order,
      activeIndex,
      overIndex
    );
    setOrder(newOrder);
  };

  const handleError = (e) => {
    const statusFamily = Math.floor(e.status / 100);
    if (statusFamily === 5) {
      setError(
        'We’re having trouble saving the changes right now - please try again.'
      );
    } else if (e.status === 400 && !e.body?.error?.startsWith('zod: ')) {
      setError(e.body.error);
    } else {
      setError(
        <>
          We’re having trouble saving the changes - please{' '}
          <InlineExternalLink
            text="log a bug"
            link="https://admin.staff.monash/feedback"
          />
        </>
      );
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateCategoriesOrder(order);
      setCatOrder(order);
      setLoading(false);
      setIsOpen(false);
      addSnackbar({
        message: 'Category order has been updated',
        type: 'success',
      });
    } catch (e) {
      setLoading(false);
      handleError(e);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      ariaLabel="Reorder Categories"
    >
      <ModalSection title="Reorder Categories" titleTabIndex={null}>
        <DndContext
          onDragEnd={(e) => {
            const { active, over } = e;
            swap(active, over);
          }}
          modifiers={[restrictToVerticalAxis]}
        >
          <div className={c.content}>
            {order.map((section) => (
              <div key={section.type} className={c.section}>
                <h4>Categories by {section.type}</h4>
                <SortableContext
                  items={section.order}
                  strategy={verticalListSortingStrategy}
                >
                  {section.order.map((id) => (
                    <SortableItem
                      key={id}
                      id={id}
                      data={{
                        section: section.type,
                      }}
                    >
                      <div className={c.catItem}>{names[id]}</div>
                    </SortableItem>
                  ))}
                </SortableContext>
              </div>
            ))}
          </div>
        </DndContext>
      </ModalSection>
      <ModalAction>
        <div className={c.modalAction}>
          {error && <Alert type="error">{error}</Alert>}
          <div className={c.modalButtons}>
            <Button
              variant="text"
              size={ctaButtonSize}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size={ctaButtonSize}
              onClick={handleSave}
              loading={loading}
            >
              Save
            </Button>
          </div>
        </div>
      </ModalAction>
    </Modal>
  );
};

export default CategoryReorder;
